import { useEffect, useState } from "react";
import {
    createFlyerTemplateDev,
    updateFlyerTemplateDev,
    defaultTemplateFields,
    fetchFlyerCategoryOptions,
    type FlyerCategoryOption,
    type FlyerTemplateInit,
    type TemplateFieldDef,
} from "../common/flyerTemplateDevService";
import TemplateFieldsEditor from "../common/TemplateFieldsEditor";
import styles from "../common/FlyerTemplateDevForm.module.css";
import { env } from "../../../config/env";

interface Props {
    onCreated: () => void;
    onError: (msg: string) => void;
    initialTemplate?: FlyerTemplateInit | null;
    onCancelEdit?: () => void;
}

const ASPECT_RATIO_OPTIONS = ["9:16", "16:9", "1:1", "4:5"];

function validateFields(fields: TemplateFieldDef[]): string | null {
    if (fields.length === 0) return "Add at least one form field.";
    const keys = new Set<string>();
    for (const f of fields) {
        if (!f.label.trim()) return "Each field needs a label.";
        if (!f.key.trim()) return "Each field needs a key.";
        if (keys.has(f.key)) return `Duplicate field key: ${f.key}`;
        keys.add(f.key);
    }
    return null;
}

function useInitState<T>(init: () => T, dep: unknown): [T, React.Dispatch<React.SetStateAction<T>>] {
    const [v, setV] = useState<T>(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setV(init()); }, [dep]);
    return [v, setV];
}

export default function FlyerTemplateDevForm({ onCreated, onError, initialTemplate, onCancelEdit }: Props) {
    const isEdit = Boolean(initialTemplate);
    const [categories, setCategories] = useState<FlyerCategoryOption[]>([]);
    const [previewFile, setPreviewFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [open, setOpen] = useState(true);
    const [pasteDetecting, setPasteDetecting] = useState(false);
    const [pasteBanner, setPasteBanner] = useState<string | null>(null);

    // All form state — reinitialise when initialTemplate changes (edit mode)
    const [categorySlug, setCategorySlug] = useInitState(() => initialTemplate?.category ?? "", initialTemplate?.id);
    const [name, setName] = useInitState(() => initialTemplate?.name ?? "", initialTemplate?.id);
    const [fields, setFields] = useInitState(() => initialTemplate?.fields ?? defaultTemplateFields(), initialTemplate?.id);
    const [ctaText, setCtaText] = useInitState(() => initialTemplate?.cta_text ?? "BOOK NOW", initialTemplate?.id);
    const [tagline, setTagline] = useInitState(() => initialTemplate?.tagline ?? "", initialTemplate?.id);
    const [style, setStyle] = useInitState(() => initialTemplate?.style ?? "", initialTemplate?.id);
    const [mood, setMood] = useInitState(() => initialTemplate?.mood ?? "", initialTemplate?.id);
    const [colorTone, setColorTone] = useInitState(() => initialTemplate?.color_tone ?? "", initialTemplate?.id);
    const [overlayStyle, setOverlayStyle] = useInitState(() => initialTemplate?.overlay_style ?? "", initialTemplate?.id);
    const [timeOfDay, setTimeOfDay] = useInitState(() => initialTemplate?.time_of_day ?? "", initialTemplate?.id);
    const [flyerType, setFlyerType] = useInitState(() => initialTemplate?.flyer_type ?? "", initialTemplate?.id);
    const [aspectRatio, setAspectRatio] = useInitState(() => initialTemplate?.aspect_ratio ?? "9:16", initialTemplate?.id);
    const [basePrompt, setBasePrompt] = useInitState(
        () => initialTemplate?.base_prompt ?? initialTemplate?.prompt_template ?? "",
        initialTemplate?.id,
    );

    useEffect(() => {
        if (isEdit) setOpen(true);
    }, [isEdit, initialTemplate?.id]);

    useEffect(() => {
        let cancelled = false;
        void fetchFlyerCategoryOptions()
            .then(data => { if (!cancelled) setCategories(data); })
            .catch(() => { if (!cancelled) onError("Could not load category list"); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBasePromptPaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const pasted = e.clipboardData.getData("text");
        if (!pasted || pasted.trim().length < 10) return;
        setPasteDetecting(true);
        setPasteBanner(null);
        try {
            const resp = await fetch(`${env.apiBaseUrl}/rag/parse-paste`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text: pasted }),
            });
            if (!resp.ok) return;
            const data = await resp.json();
            const filled: string[] = [];
            if (data.template_name) { setName(data.template_name); filled.push("name"); }
            if (data.style) { setStyle(data.style); filled.push("style"); }
            if (data.mood) { setMood(data.mood); filled.push("mood"); }
            if (data.color_tone) { setColorTone(data.color_tone); filled.push("color theme"); }
            if (data.overlay_style) { setOverlayStyle(data.overlay_style); filled.push("overlay style"); }
            if (data.time_of_day) { setTimeOfDay(data.time_of_day); filled.push("time of day"); }
            if (data.flyer_type) { setFlyerType(data.flyer_type); filled.push("layout type"); }
            if (data.aspect_ratio && ASPECT_RATIO_OPTIONS.includes(data.aspect_ratio)) { setAspectRatio(data.aspect_ratio); filled.push("aspect ratio"); }
            if (data.cta_text) { setCtaText(data.cta_text); filled.push("CTA"); }
            if (data.tagline) { setTagline(data.tagline); filled.push("tagline"); }
            if (filled.length > 0) {
                setPasteBanner(`AI auto-filled: ${filled.join(", ")} — review and adjust before saving.`);
            }
        } catch {
            // silent
        } finally {
            setPasteDetecting(false);
        }
    };

    const handleSubmit = async () => {
        if (!categorySlug || !name.trim()) {
            onError("Category and name are required.");
            return;
        }
        if (!basePrompt.trim()) {
            onError("Base prompt is required — visual foundation used when users describe the package in chat.");
            return;
        }
        if (!isEdit && !previewFile) {
            onError("Preview image is required.");
            return;
        }
        const fieldErr = validateFields(fields);
        if (fieldErr) { onError(fieldErr); return; }

        setSubmitting(true);
        try {
            const payload = {
                category_slug: categorySlug,
                name: name.trim(),
                fields,
                asset_type: "flyer" as const,
                preview_image: previewFile ?? undefined,
                base_prompt: basePrompt.trim(),
                cta_text: ctaText,
                tagline: tagline.trim(),
                style,
                mood: mood.trim(),
                color_tone: colorTone.trim(),
                flyer_type: flyerType.trim(),
                overlay_style: overlayStyle.trim(),
                time_of_day: timeOfDay.trim(),
                aspect_ratio: aspectRatio,
                prompt_template: "",
            };

            if (isEdit && initialTemplate) {
                await updateFlyerTemplateDev(initialTemplate.id, payload);
            } else {
                await createFlyerTemplateDev({ ...payload, preview_image: previewFile! });
                setName(""); setTagline(""); setBasePrompt("");
                setPreviewFile(null);
                setFields(defaultTemplateFields());
            }
            onCreated();
        } catch (e) {
            onError(e instanceof Error ? e.message : "Failed to save template.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            className={styles.panel}
            aria-label={isEdit ? `Edit flyer template: ${initialTemplate?.name ?? ""}` : "Create flyer template"}
            style={isEdit ? { borderColor: "rgba(251,191,36,0.4)", background: "linear-gradient(145deg, rgba(251,191,36,0.07) 0%, rgba(15,23,42,0.4) 100%)" } : undefined}
        >
            <button type="button" className={styles.toggle} onClick={() => setOpen(v => !v)}>
                <span>{isEdit ? `Editing: ${initialTemplate?.name ?? "template"}` : "Create flyer template"}</span>
                <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                    {isEdit && onCancelEdit && (
                        <span
                            className={styles.badge}
                            style={{ background: "rgba(239,68,68,0.2)", color: "#fca5a5", cursor: "pointer" }}
                            onClick={e => { e.stopPropagation(); onCancelEdit(); }}
                        >
                            Cancel edit
                        </span>
                    )}
                    <span className={styles.badge} style={isEdit ? { background: "rgba(251,191,36,0.2)", color: "#fcd34d" } : undefined}>
                        {isEdit ? "Edit" : "Development"}
                    </span>
                    <span className={styles.badge} style={{ background: "rgba(34,197,94,0.15)", color: "#86efac" }}>
                        🖼️ Flyer
                    </span>
                </div>
            </button>

            {open && (
                <div className={styles.body}>
                    {!isEdit && (
                        <p className={styles.hint}>
                            Saves to MongoDB <code>flyer_templates</code> and uploads the preview image to S3.
                            Users describe packages in plain language in chat — the server maps text to your fields and builds the AI prompt from the base prompt + styling (no <code>{`{placeholder}`}</code> form).
                        </p>
                    )}

                    {/* ── AI Paste Zone ── */}
                    <div className={styles.field}>
                        <label htmlFor="ft-paste-zone" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: "0.75rem", background: "rgba(99,102,241,0.18)", color: "#a5b4fc", padding: "2px 8px", borderRadius: 4, fontWeight: 600 }}>AI Auto-Fill</span>
                            Paste template config here
                        </label>
                        <div style={{ position: "relative" }}>
                            <textarea
                                id="ft-paste-zone"
                                className={styles.textarea}
                                rows={4}
                                placeholder={"Paste your template config and AI will fill all fields automatically.\n\ne.g.\nname\tSecure Health Shield\nstyle\tmodern healthcare protection\nmood\ttrustworthy, caring\ncolor_tone\tblue, white, teal\n…"}
                                onPaste={handleBasePromptPaste}
                                onChange={() => {/* read-only after paste */}}
                                value=""
                                style={{ resize: "none", fontFamily: "monospace", fontSize: "0.75rem" }}
                            />
                            {pasteDetecting && (
                                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(15,23,42,0.6)", borderRadius: 8 }}>
                                    <span style={{ color: "#a5b4fc", fontSize: "0.875rem", fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: "spin 1s linear infinite" }}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        AI reading pasted text…
                                    </span>
                                </div>
                            )}
                        </div>
                        {pasteBanner && (
                            <div style={{ marginTop: 8, display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 12px", background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.3)", borderRadius: 8, fontSize: "0.75rem", color: "#a5b4fc" }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" style={{ marginTop: 1, flexShrink: 0 }}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span style={{ flex: 1 }}>{pasteBanner}</span>
                                <button onClick={() => setPasteBanner(null)} style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", padding: 0, fontSize: "0.75rem" }}>✕</button>
                            </div>
                        )}
                    </div>

                    {/* ── Identity ── */}
                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="ft-category">Category</label>
                            <select
                                id="ft-category"
                                className={styles.select}
                                value={categorySlug}
                                onChange={e => setCategorySlug(e.target.value)}
                            >
                                <option value="">Select subcategory…</option>
                                {categories.map(c => (
                                    <option key={c.slug} value={c.slug}>
                                        {c.name} — {c.display_group}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="ft-name">Template name</label>
                            <input
                                id="ft-name"
                                className={styles.input}
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="e.g. Summer hotel promo"
                            />
                        </div>
                    </div>

                    {/* ── User input fields ── */}
                    <TemplateFieldsEditor fields={fields} onChange={setFields} />

                    {/* ── Visual design ── */}
                    <div className={styles.fieldsEditor}>
                        <span className={styles.fieldsEditorTitle}>Color theme &amp; visual design</span>
                    </div>
                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="ft-style">Visual style</label>
                            <input id="ft-style" className={styles.input} value={style} onChange={e => setStyle(e.target.value)} placeholder="e.g. modern luxury hotel" />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="ft-mood">Mood</label>
                            <input id="ft-mood" className={styles.input} value={mood} onChange={e => setMood(e.target.value)} placeholder="e.g. warm, aspirational" />
                        </div>
                    </div>
                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="ft-color">Color theme / palette</label>
                            <input id="ft-color" className={styles.input} value={colorTone} onChange={e => setColorTone(e.target.value)} placeholder="e.g. gold, cream, teal accents" />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="ft-overlay">Overlay style</label>
                            <input id="ft-overlay" className={styles.input} value={overlayStyle} onChange={e => setOverlayStyle(e.target.value)} placeholder="e.g. dark_gradient, light_vignette" />
                        </div>
                    </div>
                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="ft-tod">Lighting / time of day</label>
                            <input id="ft-tod" className={styles.input} value={timeOfDay} onChange={e => setTimeOfDay(e.target.value)} placeholder="e.g. golden hour, sunset" />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="ft-ftype">Layout type</label>
                            <input id="ft-ftype" className={styles.input} value={flyerType} onChange={e => setFlyerType(e.target.value)} placeholder="e.g. information_rich, cinematic_poetic" />
                        </div>
                    </div>
                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="ft-ratio">Aspect ratio</label>
                            <select id="ft-ratio" className={styles.select} value={aspectRatio} onChange={e => setAspectRatio(e.target.value)}>
                                {ASPECT_RATIO_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="ft-cta">CTA</label>
                            <input id="ft-cta" className={styles.input} value={ctaText} onChange={e => setCtaText(e.target.value)} />
                        </div>
                    </div>
                    <div className={styles.field}>
                        <label htmlFor="ft-tagline">Tagline</label>
                        <input id="ft-tagline" className={styles.input} value={tagline} onChange={e => setTagline(e.target.value)} placeholder="e.g. Your dream destination awaits" />
                    </div>

                    {/* ── Base prompt (visual foundation for LLM-built flyer prompt) ── */}
                    <div className={styles.field}>
                        <label htmlFor="ft-baseprompt">
                            Base prompt <span style={{ color: "#f87171", marginLeft: 2 }}>*</span>
                            <span style={{ fontWeight: 400, color: "#64748b", fontSize: "0.75rem", marginLeft: 6 }}>(visual foundation — combined with user chat details at generation time)</span>
                        </label>
                        <p className={styles.promptHelp}>
                            Defines layout, typography, and art direction for this template. When a user picks the template and types trip details in chat,
                            the backend LLM merges their message with this base prompt and the color theme fields above — users do not fill <code>{`{placeholders}`}</code>.
                        </p>
                        <textarea
                            id="ft-baseprompt"
                            className={styles.textarea}
                            rows={6}
                            value={basePrompt}
                            onChange={e => setBasePrompt(e.target.value)}
                            placeholder="e.g. Premium vertical travel flyer, luxury editorial layout, glassmorphism price card, elegant serif headlines, destination hero photography, all package copy legible in the image."
                        />
                    </div>

                    {/* ── Preview image ── */}
                    <div className={styles.field}>
                        <label htmlFor="ft-preview">
                            Preview image {isEdit && <span style={{ fontWeight: 400, color: "#64748b" }}>(leave blank to keep existing)</span>}
                        </label>
                        {isEdit && initialTemplate?.preview_image_url && !previewFile && (
                            <img
                                src={initialTemplate.preview_image_url}
                                alt="Current preview"
                                style={{ height: 80, borderRadius: 6, marginBottom: 6, objectFit: "cover", display: "block" }}
                            />
                        )}
                        <input
                            id="ft-preview"
                            type="file"
                            accept="image/*"
                            className={styles.fileInput}
                            onChange={e => setPreviewFile(e.target.files?.[0] ?? null)}
                        />
                    </div>

                    {/* ── Submit ── */}
                    <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
                        <button
                            type="button"
                            className={styles.submit}
                            disabled={submitting}
                            onClick={() => void handleSubmit()}
                        >
                            {submitting ? "Saving…" : isEdit ? "Update template" : "Save template"}
                        </button>
                        {isEdit && onCancelEdit && (
                            <button
                                type="button"
                                onClick={onCancelEdit}
                                style={{ background: "transparent", border: "1px solid rgba(148,163,184,0.3)", borderRadius: 8, padding: "0.5rem 1rem", color: "#94a3b8", fontSize: "0.8125rem", cursor: "pointer" }}
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
